import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useAuth } from '../context/AuthContext';
import { getAccount, updateAccount } from '../lib/api';

function SettingsPage() {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [preferences, setPreferences] = useState({
    orderUpdates: true,
    marketingEmails: false,
    profileVisibility: 'private',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadAccount() {
      try {
        const response = await getAccount({ userId: user.id });
        if (!isMounted) {
          return;
        }

        setPreferences(response.user.preferences);
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      loadAccount();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: '/account/settings' }} />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await updateAccount({
        userId: user.id,
        updates: { preferences },
      });
      updateUser(response.user);
      setNotice('Settings saved.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="page-card account-page">
      <div className="account-page__header">
        <div>
          <p className="profile-page__eyebrow">Account Settings</p>
          <h1 className="page-card__title">Choose how your account should behave.</h1>
          <p className="page-card__text">
            Control notifications, marketing emails, and privacy from one place.
          </p>
        </div>
        <div className="account-page__tabs">
          <Link to="/profile">Profile</Link>
          <Link to="/orders">Orders</Link>
          <Link to="/account/addresses">Addresses</Link>
          <Link to="/account/settings" className="is-active">Settings</Link>
        </div>
      </div>

      {isLoading ? <p className="products-state">Loading settings...</p> : null}
      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {notice ? <p className="orders-success">{notice}</p> : null}

      <form className="auth-form account-form" onSubmit={handleSubmit}>
        <label className="account-form__check">
          <input
            type="checkbox"
            checked={preferences.orderUpdates}
            onChange={(event) => {
              setPreferences((current) => ({ ...current, orderUpdates: event.target.checked }));
            }}
          />
          <span>Send order updates and delivery notifications</span>
        </label>
        <label className="account-form__check">
          <input
            type="checkbox"
            checked={preferences.marketingEmails}
            onChange={(event) => {
              setPreferences((current) => ({ ...current, marketingEmails: event.target.checked }));
            }}
          />
          <span>Receive promotions and new arrival emails</span>
        </label>
        <label className="auth-form__field">
          <span>Profile Visibility</span>
          <select
            value={preferences.profileVisibility}
            onChange={(event) => {
              setPreferences((current) => ({ ...current, profileVisibility: event.target.value }));
            }}
          >
            <option value="private">Private</option>
            <option value="public">Public</option>
          </select>
        </label>
        <GreenButton disabled={isSaving} type="submit" text={isSaving ? 'Saving...' : 'Save Settings'} />
      </form>
    </section>
  );
}

export default SettingsPage;
